/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { FC, ReactNode, SyntheticEvent, useId, useState } from "react";
import { useOptionalAccordionGroup } from "@/src/components/Accordion/AccordionGroupProvider";
import { Accordion as MuiAccordion, Stack, SxProps, Theme, Typography } from "@mui/material";
import { accordionStyle } from "@/src/components/Accordion/accordionStyle.ts";
import AccordionSummary from "@mui/material/AccordionSummary";
import { mergeSx } from "@/src/utils/mergeSx.ts";
import Icon from "@/src/components/Icon/Icon";
import AccordionDetails from "@mui/material/AccordionDetails";
import { IconRenderProps } from "@/src/types/IconRenderProps.ts";
import { TypographyProps } from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export type AccordionProps = {
  variant?: "outlined" | "contained" | "text";
  icon?: FC<IconRenderProps>;
  title: string;
  titlePrefix?: string;
  children: ReactNode;
  summaryTypographyVariant?: TypographyProps["variant"];
  accordionId?: string;
  sx?: SxProps<Theme>
};

export default function Accordion({ variant = "outlined", summaryTypographyVariant, titlePrefix, title, children, icon, accordionId, sx }: AccordionProps) {
  const internalAccordionId = useId();
  const accordionGroup = useOptionalAccordionGroup();
  const [internalActiveAccordionId, setInternalActiveAccordionId] = useState<string | null>(null);

  const id = accordionId || internalAccordionId;

  const setActiveAccordionId = accordionGroup ? accordionGroup.setActiveAccordionId : setInternalActiveAccordionId;
  const accordionActive = accordionGroup ? accordionGroup.activeAccordionId === id : internalActiveAccordionId === id;

  const handleToggle = (event: SyntheticEvent, isExpanded: boolean) => {
    setActiveAccordionId(isExpanded ? id : null);
  };

  const isOutlined = variant === "outlined";
  const isContained = variant === "contained";
  const isText = variant === "text";
  const hasTitlePrefix = !!titlePrefix;

  return (
    <MuiAccordion
      id={id}
      onChange={handleToggle}
      expanded={accordionActive}
      data-accordion-type={variant}
      sx={mergeSx(accordionStyle.accordionPaper, isText && accordionStyle.accordionText, sx)}
    >
      <AccordionSummary
        sx={mergeSx(
          accordionStyle.summary,
          isOutlined && accordionStyle.summaryOutlined,
          isContained && accordionStyle.summaryContained,
          isText && accordionStyle.summaryText,
          accordionActive && accordionStyle.summaryActiveOutlined,
        )}
        expandIcon={
          <ExpandMoreIcon
            sx={(theme) => ({
              color: isContained ? theme.palette.primary.main : theme.palette.text.primary,
            })}
          />
        }
        slotProps={{
          content: {
            sx: mergeSx(
              isText && accordionStyle.summaryTextContent,
              isContained && accordionStyle.summaryContainedContent,
              isOutlined && accordionStyle.summaryOutlinedContent,
            ),
          },
        }}
      >
        <Stack sx={accordionStyle.summaryContent} direction={"row"} useFlexGap>
          {!!icon && <Icon icon={icon} variant={isOutlined ? "bordered" : "standard"} active={isOutlined ? accordionActive : false} />}
          {hasTitlePrefix && (
            <Typography variant={"body1"} sx={accordionStyle.titlePrefix}>
              {titlePrefix}
            </Typography>
          )}
          <Typography variant={summaryTypographyVariant || (variant === "text" ? "h6" : "button")} sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={mergeSx(
          isOutlined && accordionStyle.detailsOutlined,
          isContained && accordionStyle.detailsContained,
          isText && accordionStyle.detailsText,
          hasTitlePrefix && accordionStyle.detailsTitlePrefix,
        )}
      >
        {children}
      </AccordionDetails>
    </MuiAccordion>
  );
}
